import { supabase } from "../supabaseClient";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export async function claimImportedAthleteData({ userId, email }) {
  const normalizedEmail = normalizeEmail(email);
  if (!userId || !normalizedEmail) {
    return {
      claimedRosterCount: 0,
      linkedProgramCount: 0,
      claimedSwotCount: 0,
      hasChanges: false
    };
  }

  const now = new Date().toISOString();
  let claimedRosterCount = 0;
  let linkedProgramCount = 0;
  let claimedSwotCount = 0;

  const { data: rosterRows, error: rosterError } = await supabase
    .from("rostered_users")
    .select("id, team_id")
    .is("user_id", null)
    .ilike("teamsnap_email", normalizedEmail);
  if (rosterError) throw rosterError;

  const rosterIds = (rosterRows || []).map((row) => row.id).filter(Boolean);
  if (rosterIds.length > 0) {
    const { error: claimRosterError } = await supabase
      .from("rostered_users")
      .update({ user_id: userId, updated_at: now })
      .in("id", rosterIds)
      .is("user_id", null);
    if (claimRosterError) throw claimRosterError;
    claimedRosterCount = rosterIds.length;

    const teamIds = Array.from(new Set((rosterRows || []).map((row) => row.team_id).filter(Boolean)));
    if (teamIds.length > 0) {
      const { data: teamRows, error: teamError } = await supabase
        .from("teams")
        .select("id, program_id")
        .in("id", teamIds);
      if (teamError) throw teamError;

      const programIds = Array.from(new Set((teamRows || []).map((row) => row.program_id).filter(Boolean)));
      if (programIds.length > 0) {
        const { data: existingFollows, error: followError } = await supabase
          .from("user_program_follows")
          .select("program_id")
          .eq("user_id", userId)
          .in("program_id", programIds);
        if (followError) throw followError;

        const followedProgramIds = new Set((existingFollows || []).map((row) => String(row.program_id)));
        const missingProgramIds = programIds.filter((programId) => !followedProgramIds.has(String(programId)));

        if (missingProgramIds.length > 0) {
          const { error: insertFollowError } = await supabase
            .from("user_program_follows")
            .insert(missingProgramIds.map((programId) => ({ user_id: userId, program_id: programId })));
          if (insertFollowError) throw insertFollowError;
          linkedProgramCount = missingProgramIds.length;
        }
      }
    }
  }

  const { data: prebuiltSwots, error: prebuiltSwotError } = await supabase
    .from("athlete_swot")
    .select("id, program_id")
    .is("athlete_id", null)
    .ilike("prebuilt_email", normalizedEmail);
  if (prebuiltSwotError) throw prebuiltSwotError;

  const prebuiltRows = prebuiltSwots || [];
  if (prebuiltRows.length > 0) {
    const programIds = Array.from(new Set(prebuiltRows.map((row) => row.program_id).filter(Boolean)));
    const { data: existingSwots, error: existingSwotError } = programIds.length
      ? await supabase
          .from("athlete_swot")
          .select("id, program_id")
          .eq("athlete_id", userId)
          .in("program_id", programIds)
      : { data: [], error: null };
    if (existingSwotError) throw existingSwotError;

    const existingProgramIds = new Set((existingSwots || []).map((row) => String(row.program_id)));
    const swotIdsToClaim = prebuiltRows
      .filter((row) => !existingProgramIds.has(String(row.program_id)))
      .map((row) => row.id)
      .filter(Boolean);

    if (swotIdsToClaim.length > 0) {
      const { error: claimSwotError } = await supabase
        .from("athlete_swot")
        .update({
          athlete_id: userId,
          status: "published",
          prebuilt_name: null,
          prebuilt_email: null,
          updated_at: now
        })
        .in("id", swotIdsToClaim)
        .is("athlete_id", null);
      if (claimSwotError) throw claimSwotError;
      claimedSwotCount = swotIdsToClaim.length;
    }
  }

  return {
    claimedRosterCount,
    linkedProgramCount,
    claimedSwotCount,
    hasChanges: claimedRosterCount > 0 || linkedProgramCount > 0 || claimedSwotCount > 0
  };
}
